import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Gift, Users, Shuffle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function SecretSantaGenerator({ eventId, isOwner }) {
  const [participants, setParticipants] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [newParticipantName, setNewParticipantName] = useState("");
  const [newParticipantEmail, setNewParticipantEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [isGenerated, setIsGenerated] = useState(false);
  const [revealedAssignments, setRevealedAssignments] = useState({});

  useEffect(() => {
    fetchSecretSanta();
  }, [eventId]);

  const fetchSecretSanta = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/secret-santa`, {
        withCredentials: true
      });
      setParticipants(response.data.participants || []);
      setAssignments(response.data.assignments || []);
      setIsGenerated(response.data.assignments && response.data.assignments.length > 0);
    } catch (error) {
      console.error("Failed to fetch secret santa:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipant = async (e) => {
    e.preventDefault();
    
    if (!newParticipantName) {
      toast.error("Name ist erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/secret-santa/participants`,
        {
          name: newParticipantName,
          email: newParticipantEmail || null
        },
        { withCredentials: true }
      );
      
      toast.success("Teilnehmer hinzugefügt!");
      setNewParticipantName("");
      setNewParticipantEmail("");
      fetchSecretSanta();
    } catch (error) {
      console.error("Failed to add participant:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen");
    }
  };

  const handleGenerateAssignments = async () => {
    if (participants.length < 3) {
      toast.error("Mindestens 3 Teilnehmer erforderlich!");
      return;
    }

    if (isGenerated && !confirm("Bereits zugeordnet! Neue Zuordnungen erstellen?")) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/events/${eventId}/secret-santa/generate`,
        {},
        { withCredentials: true }
      );
      
      setAssignments(response.data.assignments);
      setIsGenerated(true);
      setRevealedAssignments({});
      toast.success("Wichtel-Zuordnungen erstellt!");
    } catch (error) {
      console.error("Failed to generate assignments:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Generieren");
    }
  };

  const toggleReveal = (participantId) => {
    setRevealedAssignments(prev => ({
      ...prev,
      [participantId]: !prev[participantId]
    }));
  };

  const handleRemoveParticipant = async (participantId) => {
    if (!confirm("Teilnehmer wirklich entfernen?")) return;

    try {
      await axios.delete(
        `${API}/events/${eventId}/secret-santa/participants/${participantId}`,
        { withCredentials: true }
      );
      toast.success("Teilnehmer entfernt");
      fetchSecretSanta();
    } catch (error) {
      console.error("Failed to remove participant:", error);
      toast.error("Fehler beim Entfernen");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  if (!isOwner) {
    // Visitor View - show their assignment if available
    return (
      <Card className="p-6">
        <div className="text-center">
          <Gift className="w-12 h-12 text-primary mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Wichteln</h3>
          <p className="text-sm text-muted-foreground">
            Der Gastgeber organisiert das Wichteln.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Gift className="w-5 h-5 text-primary" />
          Wichtel-Generator
        </h3>
        <p className="text-sm text-muted-foreground">
          Erstelle automatisch zufällige Wichtel-Zuordnungen
        </p>
      </div>

      {/* Add Participant Form */}
      <form onSubmit={handleAddParticipant} className="mb-6 p-4 border rounded-lg space-y-3 bg-secondary/20">
        <div>
          <Label htmlFor="participant-name">Name *</Label>
          <Input
            id="participant-name"
            value={newParticipantName}
            onChange={(e) => setNewParticipantName(e.target.value)}
            placeholder="z.B. Max Mustermann"
            required
          />
        </div>
        <div>
          <Label htmlFor="participant-email">E-Mail (optional)</Label>
          <Input
            id="participant-email"
            type="email"
            value={newParticipantEmail}
            onChange={(e) => setNewParticipantEmail(e.target.value)}
            placeholder="max@example.com"
          />
          <p className="text-xs text-muted-foreground mt-1">
            E-Mail wird benötigt, um Zuordnungen automatisch zu versenden
          </p>
        </div>
        <Button type="submit" className="w-full" size="sm">
          Teilnehmer hinzufügen
        </Button>
      </form>

      {/* Participants List */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Users className="w-4 h-4" />
            Teilnehmer ({participants.length})
          </h4>
          {participants.length >= 3 && (
            <Button
              onClick={handleGenerateAssignments}
              size="sm"
              variant="default"
              className="rounded-full"
            >
              <Shuffle className="w-4 h-4 mr-2" />
              {isGenerated ? "Neu zuordnen" : "Zuordnungen erstellen"}
            </Button>
          )}
        </div>

        {participants.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Noch keine Teilnehmer</p>
            <p className="text-xs mt-1">Füge mindestens 3 Personen hinzu!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {participants.map((participant) => {
              const assignment = assignments.find(a => a.giver_id === participant.id);
              const isRevealed = revealedAssignments[participant.id];
              
              return (
                <div key={participant.id} className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{participant.name}</p>
                    {participant.email && (
                      <p className="text-xs text-muted-foreground">{participant.email}</p>
                    )}
                    {assignment && (
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => toggleReveal(participant.id)}
                          className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-primary/10 hover:bg-primary/20 transition-colors"
                        >
                          {isRevealed ? (
                            <>
                              <EyeOff className="w-3 h-3" />
                              Verbergen
                            </>
                          ) : (
                            <>
                              <Eye className="w-3 h-3" />
                              Wer beschenkt?
                            </>
                          )}
                        </button>
                        {isRevealed && (
                          <span className="text-xs font-medium text-primary">
                            → {assignment.receiver_name}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {!isGenerated && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleRemoveParticipant(participant.id)}
                      className="h-8 w-8 text-destructive"
                    >
                      ×
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {participants.length > 0 && participants.length < 3 && (
          <p className="text-xs text-amber-600 mt-3 text-center">
            ⚠️ Noch {3 - participants.length} Teilnehmer benötigt (min. 3)
          </p>
        )}
      </div>

      {isGenerated && (
        <div className="p-4 rounded-lg bg-green-50 border border-green-200">
          <p className="text-sm font-medium text-green-800 mb-1">✓ Zuordnungen erstellt!</p>
          <p className="text-xs text-green-700">
            {participants.length} Teilnehmer wurden zufällig zugeordnet.
          </p>
        </div>
      )}
    </Card>
  );
}